// Predictive search — progressive enhancement over the /search page.
// The header search trigger is a plain link to routes.search_url; this upgrades it
// to a native <dialog> with server-rendered predictive results (Predictive Search API
// + Section Rendering API). The JS only swaps the results region's innerHTML. Zero deps.
(() => {
  const dialog = document.getElementById('SearchDialog');
  if (!dialog) return;

  const input = dialog.querySelector('[data-predictive-input]');
  const results = dialog.querySelector('[data-predictive-results]');
  const initial = dialog.querySelector('[data-predictive-initial]');
  const live = dialog.querySelector('[data-predictive-live]');
  const url = dialog.dataset.url;
  const countLabel = dialog.dataset.countLabel || '%c%';

  let controller = null;
  let timer = null;
  let active = -1; // index into the current result links, -1 = none

  const links = () => [...results.querySelectorAll('[data-predictive-link]')];

  // Back to the pre-typing state: cancel any request, clear results, show Browse.
  const reset = () => {
    controller?.abort();
    clearTimeout(timer);
    results.innerHTML = '';
    active = -1;
    initial.hidden = false;
    dialog.removeAttribute('data-typing');
    live.textContent = '';
  };

  const highlight = (i) => {
    const items = links();
    if (!items.length) return;
    active = (i + items.length) % items.length; // wrap both ends
    items.forEach((el, n) => el.setAttribute('aria-selected', n === active ? 'true' : 'false'));
    items[active].scrollIntoView({ block: 'nearest' });
  };

  const run = async (q) => {
    controller?.abort(); // drop any stale in-flight request
    controller = new AbortController();
    try {
      const res = await fetch(
        `${url}?q=${encodeURIComponent(q)}&section_id=predictive-search&resources[limit]=4&resources[limit_scope]=each`,
        { signal: controller.signal }
      );
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const next = doc.querySelector('[data-predictive-results]');
      results.innerHTML = next ? next.innerHTML : '';
      initial.hidden = true;
      dialog.setAttribute('data-typing', '');
      active = -1;
      live.textContent = countLabel.replace('%c%', next?.dataset.resultCount ?? '0');
    } catch (e) {
      if (e.name !== 'AbortError') results.innerHTML = '';
    }
  };

  input.addEventListener('input', () => {
    clearTimeout(timer);
    const q = input.value.trim();
    if (q.length < 2) return reset();
    timer = setTimeout(() => run(q), 250);
  });

  input.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      highlight(active + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      highlight(active <= 0 ? -1 : active - 1); // from no/first selection, wrap to last
    } else if (e.key === 'Enter' && active > -1) {
      // Follow the highlighted result; with no highlight, Enter submits the form normally.
      const el = links()[active];
      if (el) {
        e.preventDefault();
        el.click();
      }
    }
  });

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-search-toggle]')) {
      e.preventDefault();
      // Opened from the mobile menu drawer's search button: fold the drawer away first.
      document.querySelector('.header__disclosure[open]')?.removeAttribute('open');
      if (!dialog.open) dialog.showModal();
      input.focus();
      return;
    }
    if (e.target.closest('[data-search-close]')) {
      e.preventDefault();
      dialog.close();
      return;
    }
    if (dialog.open && e.target === dialog) dialog.close(); // backdrop click
  });

  // A quick add hands off to cart-drawer.js (its own submit listener); get out of its way.
  dialog.addEventListener('submit', (e) => {
    if (e.target.closest('.predictive-search__qa')) setTimeout(() => dialog.close());
  });

  dialog.addEventListener('close', reset);
})();
